import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {InsuranceCompany} from '../../../shared/interface/insurance-company';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ReplaySubject} from 'rxjs';
import {delay, first, takeUntil} from 'rxjs/operators';
import {FileInput, FileInputComponent, FileValidator} from 'ngx-material-file-input';
import {Dictionary} from '../../../shared/interface/dictionary';
import {Uuid} from '../../../shared/interface/response/uuid';
import {HttpErrorResponse} from '@angular/common/http';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FormHelperService} from '../../../shared/services/form-helper.service';
import {IdentifierService} from '../../../shared/services/identifier.service';
import {RegistrationIdImport} from '../../../shared/interface/registration-id-import';
import {DOCUMENT} from '@angular/common';
import {FileBase64} from "../../../shared/components/file-drag-and-drop/file-drag-and-drop.component";
import {
  DictService,
  DYNAMIC_DICTIONARY_AFFILIATE_LINKS,
} from "../../../shared/services/dict.service";

@Component({
  selector: 'app-identifier-import',
  templateUrl: './identifier-import.component.html',
  styleUrls: ['./identifier-import.component.scss']
})
export class IdentifierImportComponent implements OnInit, OnDestroy {

  @ViewChild('fileInput') fileInputComponent: FileInputComponent;

  readonly DYNAMIC_DICTIONARY_AFFILIATE_LINKS = DYNAMIC_DICTIONARY_AFFILIATE_LINKS;
  importCompanyId: ReplaySubject<number> = new ReplaySubject<number>(1);
  identifierImportFormValid = false;
  identifierImportForm: FormGroup;
  companyControl: FormControl;
  insuranceCompanyControl: FormControl;
  typesControl: FormControl;
  fileControl: FormControl;
  importTypeControl: FormControl;
  fullReportControl: FormControl;
  hiperioBlockedAccountControl: FormControl;
  chronieLoginControl: FormControl;
  hiperioDataControl: FormControl;
  affiliateLinksControl: FormControl;
  typeKeys: string[] = [];
  fileMaxSize = 5242880;
  selectedTypes: Dictionary[] = [];
  processingRequest = false;
  formErrors = {};
  registrationIdImport?: RegistrationIdImport;
  affiliateLinksSelected: Dictionary[] = [];
  affiliateLinksTypesControl: FormControl;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private fb: FormBuilder,
    private snack: MatSnackBar,
    private formHelper: FormHelperService,
    private identifierService: IdentifierService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.identifierImportForm = fb.group({
      company: this.companyControl = fb.control('', [Validators.required]),
      insuranceCompany: this.insuranceCompanyControl = fb.control('', [Validators.required]),
      types: this.typesControl = fb.control([], []),
      changeToRegistered: fb.control(false),
      changeToDeregistered: fb.control(false),
      fileContent: fb.control(''),
      file: this.fileControl = fb.control('', [Validators.required]),
      chronieLogin: this.chronieLoginControl = fb.control(false),
      hiperioData: this.hiperioDataControl = fb.control(false),
      affiliateLinks: this.affiliateLinksControl = fb.control(false),
      fullReport : this.fullReportControl = fb.control(false),
      importType: this.importTypeControl = fb.control('', [Validators.required]),
      hiperioBlockedAccount: this.hiperioBlockedAccountControl = fb.control(false),
      affiliateLinksTypes: this.affiliateLinksTypesControl = fb.control([], []),
    });
    this.hiperioBlockedAccountControl.disable();

  }

  ngOnInit(): void {
    this.identifierImportForm.get('file').valueChanges
      .pipe(
        takeUntil(this.destroyed$))
      .subscribe((file: FileInput) => {
       if (file) {
          this.identifierImportForm.get('fileContent').reset('');
          this.fileControl.value.forEach((fileToRead: File) => {
            const reader = new FileReader();
            reader.readAsDataURL(fileToRead);
            reader.onload = () => {
              this.identifierImportForm.get('fileContent').setValue(
                this.identifierImportForm.get('fileContent').value +
                (this.identifierImportForm.get('fileContent').value ? ', ' : '') +
                (/base64,(.+)/.exec(reader.result.toString())[1])
              );
            };
          });
        }
      });


    this.identifierImportForm.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        delay(0)
      )
      .subscribe(() => {
        this.identifierImportFormValid = this.identifierImportForm.valid;
      });

    this.companyControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((companyId: number) => this.importCompanyId.next(companyId));

    this.importTypeControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((type: string) => {
        this.hiperioDataControl.setValue(type === 'hiperioData');
        this.chronieLoginControl.setValue(type === 'chronieLogin');
        this.affiliateLinksControl.setValue(type === 'affiliateLinks');

        this.updateForm();
      });
    this.affiliateLinksTypesControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((dicts: Dictionary[]) => {
        this.affiliateLinksSelected = dicts || [];
      });
  }

  fileSelected($event: FileBase64): void {
    if ($event === null){
      this.identifierImportForm.get('file').reset('');
    }

  }

  updateForm(): void {
    if (this.hiperioDataControl.value ) {
      this.hiperioBlockedAccountControl.enable();
    } else {
      this.hiperioBlockedAccountControl.disable();
    }

    if (this.hiperioDataControl.value || this.chronieLoginControl.value) {
      this.companyControl.disable();
      this.insuranceCompanyControl.disable();
      this.identifierImportForm.get('changeToRegistered').disable();
      this.identifierImportForm.get('changeToDeregistered').disable();
      this.fullReportControl.disable();
    } else if (this.affiliateLinksControl.value) {
      this.companyControl.enable();
      this.insuranceCompanyControl.enable();
      this.identifierImportForm.get('changeToRegistered').disable();
      this.identifierImportForm.get('changeToDeregistered').disable();
      this.fullReportControl.disable();
    } else {
      this.companyControl.enable();
      this.insuranceCompanyControl.enable();
      this.identifierImportForm.get('changeToRegistered').enable();
      this.identifierImportForm.get('changeToDeregistered').enable();
      this.fullReportControl.enable();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  insuranceCompanyChanged(insuranceCompany: InsuranceCompany): void {
    this.typeKeys = insuranceCompany.registrationIdTypes;
    this.typesControl.reset([]);
    this.onTypesChange([]);
  }

  onTypesChange(dicts: Dictionary[]): void {
    this.selectedTypes = dicts;
  }

  loadImport(importId: string): void {
    this.identifierService.getImport(importId)
      .pipe(delay(1000))
      .subscribe((registrationIdImport: RegistrationIdImport) => {
        this.registrationIdImport = registrationIdImport;

        if (registrationIdImport.progress !== 100) {
          this.loadImport(registrationIdImport.uuid);
        } else {
          this.snack.open('Identyfikatory zostały zaimportowane', '', {
            duration: 2000,
          });
        }
      });
  }

  import(): void {
    if (!this.identifierImportForm.valid) {
      this.identifierImportForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }

    this.processingRequest = true;
    this.formHelper.removeBackendErrorState(this.identifierImportForm, this.formErrors);
    this.formErrors = {};

    this.identifierImportForm.value.affiliateLinksTypes =
      (this.identifierImportForm.value.affiliateLinksTypes || []).map((item: any) => item.id);

    this.identifierService.import(this.identifierImportForm.value)
      .pipe(
        first()
      )
      .subscribe((response: Uuid) => {
        this.snack.open('Rozpoczynanie importowania identyfikatorów', '', {
          duration: 2000,
        });
        this.processingRequest = false;
        this.loadImport(response.uuid);
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;

        this.snack.open('Podczas importu wystąpił błąd', error.status.toString(), {
          duration: 2000,
        });

        if (error.status === 400) {
          this.formErrors = error.error.error.list;
          this.formHelper.setBackendErrorState(this.identifierImportForm, this.formErrors);
        }
      })
    ;
  }
  copyMessage(val: string): void{
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
    this.snack.open('Skopiowano do schowka', '', {duration: 2000});
  }

  resetFileValue(): void {
    this.fileControl.setValue('');
    this.fileInputComponent.clear();
  }
}
